import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Label,
  Textarea,
} from "@jem-open/jem-ui";
import { CheckCircle2 } from "lucide-react";
import { AttendanceException } from "../services/model/AttendanceException";
import {
  exceptionTypeLabels,
  isNewExceptionType,
} from "../constants/exceptionTypeConfig";
import {
  getExceptionDetailsSummary,
  getExceptionShiftLabel,
  getShiftTimeRange,
} from "../utils/exceptionDisplayUtils";

interface GenericResolveModalProps {
  exception: AttendanceException;
  open: boolean;
  onClose: () => void;
  onResolve: (id: string, action: string, reason?: string) => void;
}

const TITLE_BY_TYPE: Record<string, string> = {
  late_arrival: "Late Arrival",
  replacement: "Replacement",
  wrong_site: "Wrong Site",
};

const DESCRIPTION_BY_TYPE: Record<string, (name: string) => string> = {
  late_arrival: (name) =>
    `Acknowledge the late arrival for ${name} and close out this exception.`,
  replacement: (name) =>
    `Acknowledge the replacement entry for ${name} and close out this exception.`,
  wrong_site: (name) =>
    `Acknowledge the wrong-site clock event for ${name} and close out this exception.`,
};

const GenericResolveModal: React.FC<GenericResolveModalProps> = ({
  exception,
  open,
  onClose,
  onResolve,
}) => {
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) setNotes("");
  }, [exception.id, open]);

  const typeLabel = isNewExceptionType(exception.exception_type)
    ? exceptionTypeLabels[exception.exception_type]
    : TITLE_BY_TYPE[exception.exception_type] ?? exception.exception_type;
  const description =
    DESCRIPTION_BY_TYPE[exception.exception_type]?.(exception.employee_name) ??
    `Close out this ${typeLabel.toLowerCase()} exception for ${
      exception.employee_name
    }.`;
  const detailSummary = getExceptionDetailsSummary(exception);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-xs">
            <CheckCircle2 size={18} className="text-emerald-500" />
            {typeLabel} — Resolve Exception
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-md py-sm">
          <div className="rounded-lg border border-greyscale-border p-sm space-y-xxs">
            <p className="text-sm">
              <span className="font-semibold">Employee:</span>{" "}
              {exception.employee_name}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Site:</span>{" "}
              {exception.location_name} — {exception.region_name}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Shift:</span>{" "}
              {getExceptionShiftLabel(exception)} (
              {getShiftTimeRange(exception.shift_start, exception.shift_end)})
            </p>
            {exception.timestamp && (
              <p className="text-sm">
                <span className="font-semibold">Time:</span>{" "}
                {exception.timestamp}
              </p>
            )}
            {detailSummary && (
              <p className="text-sm text-greyscale-text-caption">
                {detailSummary}
              </p>
            )}
          </div>

          <div className="space-y-xxs">
            <Label
              htmlFor="generic-resolve-notes"
              className="text-sm font-semibold"
            >
              Notes (optional)
            </Label>
            <Textarea
              id="generic-resolve-notes"
              placeholder="Add any context or follow-up..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            onClick={() => {
              onResolve(exception.id, "approve", notes.trim() || undefined);
              onClose();
            }}
          >
            Mark Resolved
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenericResolveModal;
